import React, { useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import html2canvas from 'html2canvas';

import FloatingAIAssistant from '../AI/FloatingAIAssistant';
import useAuth from '../../hooks/useAuth';
import LeadGoalProgress from './LeadGoalProgress';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

// Shared by the campaign-level table and the ad-level creatives grid — extracted
// so the 'geral' level can run it twice (once per dataset) without duplicating
// the action-parsing logic.
function buildProcessedRows(rawData, rawPreviousData, isAdRows, conversionType) {
  return (rawData || []).map(item => {
    let targetMetric = 0;
    let prevTargetMetric = 0;

    const prevItem = rawPreviousData?.find(p => isAdRows ? (p.ad_id === item.ad_id || p.ad_name === item.ad_name) : (p.campaign_id === item.campaign_id || p.campaign_name === item.campaign_name));

    if (item.actions && Array.isArray(item.actions)) {
      item.actions.forEach(action => {
        const type = action.action_type;
        const val = parseInt(action.value) || 0;

        if (conversionType === 'lead') {
          if (type === 'lead' || type === 'onsite_conversion.lead_grouped' || type.includes('lead')) targetMetric += val;
        } else if (conversionType === 'message') {
          if (type === 'onsite_conversion.messaging_conversation_started_7d' || type === 'contact') targetMetric += val;
        } else if (conversionType === 'click') {
          if (type === 'link_click') targetMetric += val;
        } else if (conversionType === 'conversion') {
          if (type.includes('conversion') || type === 'custom_event') targetMetric += val;
        } else if (conversionType === 'purchase') {
          if (type === 'purchase' || type.includes('purchase') || type === 'offsite_conversion.fb_pixel_purchase') targetMetric += val;
        }
      });
    }

    const spend = parseFloat(item.spend) || 0;
    const clicks = parseInt(item.inline_link_clicks || item.clicks) || 0;

    // If conversion is click, fallback to link clicks if actions array is empty
    if (conversionType === 'click' && targetMetric === 0) {
      targetMetric = clicks;
    }

    const cpaTarget = targetMetric > 0 ? (spend / targetMetric).toFixed(2) : 0;

    // Previous data extract
    if (prevItem && prevItem.actions && Array.isArray(prevItem.actions)) {
      prevItem.actions.forEach(action => {
        const type = action.action_type;
        const val = parseInt(action.value) || 0;
        if (conversionType === 'lead') {
          if (type === 'lead' || type === 'onsite_conversion.lead_grouped' || type.includes('lead')) prevTargetMetric += val;
        } else if (conversionType === 'message') {
          if (type === 'onsite_conversion.messaging_conversation_started_7d' || type === 'contact') prevTargetMetric += val;
        } else if (conversionType === 'click') {
          if (type === 'link_click') prevTargetMetric += val;
        } else if (conversionType === 'conversion') {
          if (type.includes('conversion') || type === 'custom_event') prevTargetMetric += val;
        } else if (conversionType === 'purchase') {
          if (type === 'purchase' || type.includes('purchase') || type === 'offsite_conversion.fb_pixel_purchase') prevTargetMetric += val;
        }
      });
    }

    const prevSpend = parseFloat(prevItem?.spend) || 0;
    const prevClicks = parseInt(prevItem?.inline_link_clicks || prevItem?.clicks) || 0;
    if (conversionType === 'click' && prevTargetMetric === 0) prevTargetMetric = prevClicks;

    const prevCpaTarget = prevTargetMetric > 0 ? prevSpend / prevTargetMetric : 0;

    return {
      ...item,
      clicks,
      targetMetric,
      cpaTarget,
      prevSpend,
      prevClicks,
      prevTargetMetric,
      prevCpaTarget
    };
  });
}

const ReportVisualization = ({
  data,
  previousData,
  secondaryData,
  level,
  conversionType,
  onBack,
  onExportCsv,
  onShare,
  demographicsData,
  campaignSummary,
  clinicSection,
  leadGoal,
  leadGoalAchieved,
  leadGoalLegend,
  showFunnelAndGoals = true,
  readOnly = false
}) => {
  const reportRef = useRef(null);
  const [sortConfig, setSortConfig] = useState({ key: 'spend', direction: 'desc' });
  const { user } = useAuth();

  if (!data || data.length === 0) {
    return (
      <div className="space-y-8">
        <div className="text-center p-12 bg-surface-container-lowest rounded-xl shadow-[0_8px_32px_rgba(25,28,30,0.04)]">
          <h2 className="text-2xl font-bold font-headline text-on-surface mb-2">Nenhum Dado Disponível</h2>
          <p className="text-secondary font-body mb-6">Não há dados de campanha para o período ou filtros selecionados.</p>
          <button onClick={onBack} className="primary-pill-gradient text-white rounded-full py-2 px-6 font-bold flex mx-auto items-center hover:opacity-90 transition-all font-headline">
            Voltar as Opções
          </button>
        </div>
        {showFunnelAndGoals && leadGoal && (
          <div className="relative z-10">
            <LeadGoalProgress
              goal={leadGoal}
              achieved={leadGoalAchieved}
              conversionType={conversionType}
              readOnly={readOnly}
              legend={leadGoalLegend}
            />
          </div>
        )}
        {/* Clinic data can still be relevant even with no Meta campaign delivery */}
        {clinicSection}
      </div>
    );
  }

  // Helper to extract stats
  const extractStats = (rawData) => {
    if (!rawData) return { spend: 0, clicks: 0, targetMetric: 0, ctr: 0, cpc: 0, cpa: 0, impressions: 0 };
    
    let totalSpend = 0;
    let totalImpressions = 0;
    let totalClicks = 0;
    let totalTargetMetric = 0;

    rawData.forEach(item => {
      let metric = 0;
      if (item.actions && Array.isArray(item.actions)) {
        item.actions.forEach(action => {
          const type = action.action_type;
          const val = parseInt(action.value) || 0;
          if (conversionType === 'lead') {
            if (type === 'lead' || type === 'onsite_conversion.lead_grouped' || type.includes('lead')) metric += val;
          } else if (conversionType === 'message') {
            if (type === 'onsite_conversion.messaging_conversation_started_7d' || type === 'contact') metric += val;
          } else if (conversionType === 'click') {
            if (type === 'link_click') metric += val;
          } else if (conversionType === 'conversion') {
            if (type.includes('conversion') || type === 'custom_event') metric += val;
          } else if (conversionType === 'purchase') {
            if (type === 'purchase' || type.includes('purchase') || type === 'offsite_conversion.fb_pixel_purchase') metric += val;
          }
        });
      }

      const c = parseInt(item.inline_link_clicks || item.clicks) || 0;
      if (conversionType === 'click' && metric === 0) metric = c;

      totalSpend += parseFloat(item.spend) || 0;
      totalImpressions += parseInt(item.impressions) || 0;
      totalClicks += c;
      totalTargetMetric += metric;
    });

    return {
      spend: totalSpend,
      impressions: totalImpressions,
      clicks: totalClicks,
      targetMetric: totalTargetMetric,
      ctr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
      cpc: totalClicks > 0 ? totalSpend / totalClicks : 0,
      cpa: totalTargetMetric > 0 ? totalSpend / totalTargetMetric : 0
    };
  };

  const currentStats = useMemo(() => extractStats(data), [data, conversionType]);
  const prevStats = useMemo(() => extractStats(previousData), [previousData, conversionType]);
  const headerStats = useMemo(() => {
    if (level === 'ad' && campaignSummary?.length > 0) return extractStats(campaignSummary);
    return currentStats;
  }, [level, campaignSummary, currentStats, conversionType]);

  const calcDelta = (curr, prev, inverse = false) => {
    if (!prev || prev === 0) return null;
    const diff = curr - prev;
    const percent = (diff / prev) * 100;
    
    // For costs (inverse=true), a decrease is positive. For normal metrics, an increase is positive.
    const isPositive = inverse ? diff < 0 : diff > 0;
    const absPercent = Math.abs(percent).toFixed(1);
    const icon = diff > 0 ? 'arrow_upward' : diff < 0 ? 'arrow_downward' : 'remove';
    const colorClass = diff === 0 
        ? 'text-slate-500 bg-slate-100' 
        : isPositive ? 'text-emerald-700 bg-emerald-100' : 'text-red-700 bg-red-100';
        
    return (
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 mt-2 rounded text-[11px] font-extrabold font-headline tracking-wide ${colorClass}`} title="Comparado ao período anterior">
        <span className="material-symbols-outlined text-[12px]">{icon}</span>
        {absPercent}%
      </span>
    );
  };

  const isAdLevel = level === 'ad';
  const isGeral = level === 'geral';

  // Pre-process data for tables and charts. 'data' is campaign-level rows for
  // 'campaign'/'geral', ad-level rows for 'ad'. 'secondaryData' (ad-level rows)
  // is only populated when level === 'geral', for the creatives section.
  const processedData = useMemo(
    () => buildProcessedRows(data, previousData, isAdLevel, conversionType),
    [data, previousData, isAdLevel, conversionType]
  );

  const adProcessedData = useMemo(
    () => (isGeral && secondaryData?.length ? buildProcessedRows(secondaryData, [], true, conversionType) : []),
    [isGeral, secondaryData, conversionType]
  );
  const adSortedData = useMemo(
    () => [...adProcessedData].sort((a, b) => (parseFloat(b.spend) || 0) - (parseFloat(a.spend) || 0)),
    [adProcessedData]
  );

  const sortedData = useMemo(() => {
    let sortableItems = [...processedData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Handle numeric values that might be strings
        if (['spend', 'impressions', 'clicks', 'targetMetric', 'cpaTarget'].includes(sortConfig.key)) {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else {
          // Fallback for strings
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [processedData, sortConfig]);

  const requestSort = (key) => {
    let direction = 'desc'; // Default to desc for metrics
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'unfold_more';
    return sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  };

  // Aggregate stats mapping back to original variables
  const totalSpend = headerStats.spend;
  const totalClicks = headerStats.clicks;
  const totalTargetMetric = headerStats.targetMetric;
  const avgCtr = headerStats.ctr.toFixed(2);
  const avgCpc = headerStats.cpc.toFixed(2);
  const avgCpaTarget = headerStats.cpa.toFixed(2);

  // Chart Data preparation
  const labels = processedData.map(c => isAdLevel ? (c.ad_name || 'Anúncio Desconhecido') : (c.campaign_name || 'Campanha Desconhecida'));
  const spendData = processedData.map(c => parseFloat(c.spend) || 0);

  const doughnutData = {
    labels: labels.map(l => l.length > 20 ? l.substring(0, 20) + '...' : l),
    datasets: [{
      data: spendData,
      backgroundColor: ['#cd19f9', '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'],
      borderWidth: 0,
      hoverOffset: 4
    }],
  };

  const metricLabelMapping = {
    lead: 'Leads',
    message: 'Conversas',
    click: 'Cliques',
    conversion: 'Conversões',
    purchase: 'Compras'
  };
  const metricLabel = metricLabelMapping[conversionType] || 'Conversões';

  const conversionBarData = {
    labels: labels.map(l => l.length > 20 ? l.substring(0, 20) + '...' : l),
    datasets: [
      {
        label: 'Cliques',
        data: processedData.map(c => c.clicks),
        backgroundColor: 'rgba(205, 25, 249, 0.3)', // Light primary
        borderRadius: 4,
      },
      {
        label: metricLabel,
        data: processedData.map(c => c.targetMetric),
        backgroundColor: '#cd19f9', // Primary
        borderRadius: 4,
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          font: {
            family: "'Manrope', sans-serif"
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
      y: {
        border: {
          dash: [4, 4]
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      }
    }
  };

  const handleExportPng = async () => {
    if (!reportRef.current) return;
    try {
      // Temporarily force 1920px width for rendering
      const originalWidth = reportRef.current.style.width;
      const originalMaxWidth = reportRef.current.style.maxWidth;
      reportRef.current.style.width = '1920px';
      reportRef.current.style.maxWidth = '1920px';

      const canvas = await html2canvas(reportRef.current, { 
        scale: 2, 
        useCORS: true,
        windowWidth: 1920,
        width: 1920
      });

      // Restore original styles
      reportRef.current.style.width = originalWidth;
      reportRef.current.style.maxWidth = originalMaxWidth;

      const imgData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imgData;
      link.download = `relatorio_${level}_${conversionType}_${new Date().toISOString().split('T')[0]}.png`;
      link.click();
    } catch (err) {
      console.error('Failed to export PNG', err);
      if (reportRef.current) {
        reportRef.current.style.width = '';
        reportRef.current.style.maxWidth = '';
      }
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-16">
      {(user?.roleType === 'ADMIN' || user?.aiAssistantEnabled !== false) && (
        <FloatingAIAssistant campaignData={processedData} />
      )}
      
      <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
        <div>
          <h2 className="font-headline font-extrabold text-3xl tracking-tight text-on-surface">Visualização de Relatório</h2>
          <p className="text-secondary font-body mt-1">Revise desempenho, cliques e {metricLabel}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={onBack} className="px-6 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all text-on-surface flex items-center gap-1">
             <span className="material-symbols-outlined text-sm">arrow_back</span> Voltar
          </button>
          <button onClick={() => onExportCsv(processedData)} className="px-6 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-bold hover:bg-emerald-200 transition-all flex items-center gap-1">
             <span className="material-symbols-outlined text-sm">csv</span> Exportar CSV
          </button>
          <button onClick={handleExportPng} className="primary-pill-gradient text-white px-6 py-2 rounded-full text-sm font-bold hover:opacity-90 transition-all shadow-sm flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">image</span> Exportar PNG
          </button>
          <button onClick={onShare} className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 px-6 py-2 rounded-full text-sm font-bold transition-all shadow-sm flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">share</span> Compartilhar
          </button>
        </div>
      </div>

      {/* Snapshot Reference Container */}
      <div ref={reportRef} className="bg-surface p-8 border border-outline-variant/30 rounded-2xl relative overflow-hidden">
        
        {/* Marca d'água */}
        <div 
           className="absolute inset-0 pointer-events-none z-0" 
           style={{ 
              backgroundImage: 'url(/WATERMARK.png)', 
              backgroundRepeat: 'repeat', 
              backgroundSize: '500px', 
              opacity: 0.1 
           }} 
        />

        {/* Branding Print Header */}
        <div className="flex justify-between items-center mb-8 relative z-10">
           <div>
              <h2 className="text-2xl font-extrabold font-headline text-on-surface">Relatório de Desempenho</h2>
              <p className="text-sm text-secondary font-body mt-1">Comparativo de Campanhas - Meta Ads</p>
           </div>
           <img src="/LOGO.png" alt="Lumina Reporting" className="h-10 w-auto" crossOrigin="anonymous" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 mb-10 relative z-10">
          <div className="bg-surface-container-lowest p-6 rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20 col-span-2 lg:col-span-1">
            <p className="text-sm text-secondary font-bold font-headline mb-2 uppercase tracking-wide flex items-center gap-1">
              <span className="material-symbols-outlined text-[16px]">payments</span> Gasto Total
            </p>
            <p className="text-3xl font-extrabold font-headline text-on-surface">R$ {totalSpend.toFixed(2)}</p>
            {calcDelta(totalSpend, prevStats.spend, true)}
          </div>
          <div className="bg-surface-container-lowest p-6 rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20 col-span-2 lg:col-span-1">
            <p className="text-sm text-secondary font-bold font-headline mb-2 uppercase tracking-wide flex items-center gap-1">
              <span className="material-symbols-outlined text-[16px]">ads_click</span> Cliques Totais
            </p>
            <p className="text-3xl font-extrabold font-headline text-on-surface">{totalClicks.toLocaleString()}</p>
            {calcDelta(totalClicks, prevStats.clicks, false)}
          </div>
          <div className="bg-primary/5 p-6 rounded-xl border border-primary/20 col-span-2 md:col-span-4 lg:col-span-1">
            <p className="text-sm text-primary font-bold font-headline mb-2 uppercase tracking-wide flex items-center gap-1">
              <span className="material-symbols-outlined text-[16px]">target</span> Total de {metricLabel}
            </p>
            <p className="text-3xl font-extrabold font-headline text-primary flex items-baseline gap-2">
              {totalTargetMetric.toLocaleString()} <span className="text-sm font-bold text-primary/60">CPA R$ {avgCpaTarget} máx.</span>
            </p>
            {calcDelta(totalTargetMetric, prevStats.targetMetric, false)}
          </div>
          <div className="bg-surface-container-lowest p-6 rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20 col-span-1">
            <p className="text-sm text-secondary font-bold font-headline mb-2 uppercase tracking-wide flex items-center gap-1">
               <span className="material-symbols-outlined text-[16px]">percent</span> CTR Médio
            </p>
            <p className="text-2xl font-extrabold font-headline text-on-surface">{avgCtr}%</p>
            {calcDelta(currentStats.ctr, prevStats.ctr, false)}
          </div>
          <div className="bg-surface-container-lowest p-6 rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20 col-span-1">
            <p className="text-sm text-secondary font-bold font-headline mb-2 uppercase tracking-wide flex items-center gap-1">
               <span className="material-symbols-outlined text-[16px]">attach_money</span> CPC Médio
            </p>
            <p className="text-2xl font-extrabold font-headline text-on-surface">R$ {avgCpc}</p>
            {calcDelta(currentStats.cpc, prevStats.cpc, true)}
          </div>
        </div>

        {showFunnelAndGoals && leadGoal && (
          <div className="relative z-10">
            <LeadGoalProgress
              goal={leadGoal}
              achieved={leadGoalAchieved}
              conversionType={conversionType}
              readOnly={readOnly}
              legend={leadGoalLegend}
            />
          </div>
        )}

        {clinicSection && (
          <div className="mb-10 relative z-10">
            {clinicSection}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-10 relative z-10">
          <div className="bg-surface-container-lowest p-8 rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20 xl:col-span-2">
            <h3 className="text-xl font-extrabold font-headline text-on-surface mb-6">Funil de Conversão ({metricLabel})</h3>
            <div className="h-80 w-full relative">
              <Bar data={conversionBarData} options={chartOptions} />
            </div>
          </div>
          <div className="bg-surface-container-lowest p-8 rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20">
            <h3 className="text-xl font-extrabold font-headline text-on-surface mb-6">Distribuição de Gastos</h3>
            <div className="h-80 w-full flex justify-center items-center pb-4 relative">
              <Doughnut data={doughnutData} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { font: { family: "'Manrope', sans-serif" } } } } }} />
            </div>
          </div>
        </div>

        {/* Demographics Section */}
        {demographicsData && (demographicsData.byAge?.length > 0 || demographicsData.byGender?.length > 0) && (
          <div className="relative z-10">
            <h3 className="text-xl font-extrabold font-headline text-on-surface mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-[22px]">group</span>
              Dados Demográficos
            </h3>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Age Breakdown */}
              {demographicsData.byAge?.length > 0 && (
                <div className="bg-surface-container-lowest p-6 rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20">
                  <h4 className="text-base font-bold font-headline text-on-surface mb-4 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[16px] text-primary">cake</span>
                    Distribuição por Faixa Etária
                  </h4>
                  <div className="h-56">
                    <Bar
                      data={{
                        labels: demographicsData.byAge.map(d => d.age),
                        datasets: [
                          {
                            label: 'Gasto (R$)',
                            data: demographicsData.byAge.map(d => parseFloat(d.spend) || 0),
                            backgroundColor: 'rgba(205, 25, 249, 0.7)',
                            borderRadius: 6,
                          },
                          {
                            label: 'Conversões',
                            data: demographicsData.byAge.map(d => parseFloat(d.conversions) || 0),
                            backgroundColor: 'rgba(99, 102, 241, 0.7)',
                            borderRadius: 6,
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom', labels: { font: { family: "'Manrope', sans-serif", size: 11 } } } },
                        scales: { x: { grid: { display: false } }, y: { border: { dash: [4, 4] } } }
                      }}
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    {demographicsData.byAge.slice(0, 6).map(d => (
                      <div key={d.age} className="bg-surface-container p-2 rounded-lg">
                        <p className="text-[10px] font-bold text-primary uppercase">{d.age}</p>
                        <p className="text-xs font-bold text-on-surface">{parseInt(d.impressions || 0).toLocaleString()} imp.</p>
                        <p className="text-[10px] text-secondary">{parseInt(d.clicks || 0).toLocaleString()} cliques</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gender Breakdown */}
              {demographicsData.byGender?.length > 0 && (
                <div className="bg-surface-container-lowest p-6 rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20">
                  <h4 className="text-base font-bold font-headline text-on-surface mb-4 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[16px] text-primary">wc</span>
                    Distribuição por Gênero
                  </h4>
                  <div className="h-56 flex items-center justify-center">
                    <Doughnut
                      data={{
                        labels: demographicsData.byGender.map(d => d.gender === 'M' ? 'Masculino' : d.gender === 'F' ? 'Feminino' : 'Outro'),
                        datasets: [{
                          data: demographicsData.byGender.map(d => parseFloat(d.spend) || 0),
                          backgroundColor: ['#6366f1', '#cd19f9', '#10b981'],
                          borderWidth: 0,
                          hoverOffset: 6
                        }]
                      }}
                      options={{
                        maintainAspectRatio: false,
                        plugins: { legend: { position: 'bottom', labels: { font: { family: "'Manrope', sans-serif", size: 12 } } } }
                      }}
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    {demographicsData.byGender.map(d => {
                      const label = d.gender === 'M' ? 'Masculino' : d.gender === 'F' ? 'Feminino' : 'Outro';
                      const color = d.gender === 'M' ? 'text-indigo-600 bg-indigo-50' : d.gender === 'F' ? 'text-purple-600 bg-purple-50' : 'text-emerald-600 bg-emerald-50';
                      return (
                        <div key={d.gender} className={`p-3 rounded-lg ${color}`}>
                          <p className="text-[10px] font-extrabold uppercase">{label}</p>
                          <p className="text-sm font-bold">R$ {parseFloat(d.spend || 0).toFixed(2)}</p>
                          <p className="text-[10px]">{parseInt(d.clicks || 0).toLocaleString()} cliques</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Top Audiences */}
            {demographicsData.topAudiences?.length > 0 && (
              <div className="mt-6 bg-surface-container-lowest p-6 rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20">
                <h4 className="text-base font-bold font-headline text-on-surface mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-[16px] text-primary">star</span>
                  Top Audiências (Idade + Gênero)
                </h4>
                <div className="overflow-x-auto">
                  <table className="table w-full text-sm">
                    <thead>
                      <tr className="border-b border-outline-variant/20">
                        <th className="pb-2 text-xs font-bold text-secondary text-left">Faixa Etária</th>
                        <th className="pb-2 text-xs font-bold text-secondary text-left">Gênero</th>
                        <th className="pb-2 text-xs font-bold text-secondary text-right">Impressões</th>
                        <th className="pb-2 text-xs font-bold text-secondary text-right">Cliques</th>
                        <th className="pb-2 text-xs font-bold text-secondary text-right">Gasto</th>
                        <th className="pb-2 text-xs font-bold text-secondary text-right">Conversões</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demographicsData.topAudiences.slice(0, 8).map((row, i) => (
                        <tr key={i} className="border-b border-outline-variant/10 hover:bg-surface-container-low transition-colors">
                          <td className="py-3 font-bold text-on-surface">{row.age}</td>
                          <td className="py-3 text-secondary">{row.gender === 'M' ? 'Masculino' : row.gender === 'F' ? 'Feminino' : 'Outro'}</td>
                          <td className="py-3 text-right text-secondary">{parseInt(row.impressions || 0).toLocaleString()}</td>
                          <td className="py-3 text-right text-secondary">{parseInt(row.clicks || 0).toLocaleString()}</td>
                          <td className="py-3 text-right font-bold text-on-surface">R$ {parseFloat(row.spend || 0).toFixed(2)}</td>
                          <td className="py-3 text-right font-bold text-primary">{parseFloat(row.conversions || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {(isAdLevel || isGeral) && (
          <div className="relative z-10 mb-10">
            <div className="flex items-center justify-between mb-6 px-1">
              <h3 className="text-lg font-bold font-headline text-on-surface">Criativos</h3>
              <span className="text-sm text-secondary font-body">{(isGeral ? adSortedData : sortedData).length} anúncios</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {(isGeral ? adSortedData : sortedData).map((row, i) => {
                const rowCtr = parseInt(row.impressions) > 0 ? ((row.clicks / parseInt(row.impressions)) * 100).toFixed(2) : '0.00';
                const rankColor = i === 0 ? 'bg-primary text-white' : i === 1 ? 'bg-slate-600 text-white' : i === 2 ? 'bg-amber-500 text-white' : 'bg-surface-container text-secondary';
                return (
                  <div key={i} className="bg-surface-container-lowest rounded-xl border border-outline-variant/20 overflow-hidden shadow-sm hover:shadow-md transition-all group/card">
                    <div className="relative aspect-square bg-surface-container">
                      {(row.thumbnail_url || row.image_url) ? (
                        row.preview_shareable_link ? (
                          <a href={row.preview_shareable_link} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                            <img src={row.thumbnail_url || row.image_url} alt="" className="w-full h-full object-cover group-hover/card:opacity-90 transition-opacity" crossOrigin="anonymous" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/card:bg-black/20 transition-colors">
                              <span className="material-symbols-outlined text-white opacity-0 group-hover/card:opacity-100 transition-opacity text-[32px]">open_in_new</span>
                            </div>
                          </a>
                        ) : (
                          <img src={row.thumbnail_url || row.image_url} alt="" className="w-full h-full object-cover" crossOrigin="anonymous" />
                        )
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="material-symbols-outlined text-[40px] text-outline-variant">image_not_supported</span>
                        </div>
                      )}
                      <span className={`absolute top-2 left-2 w-6 h-6 rounded-full text-[11px] font-extrabold flex items-center justify-center shadow-sm ${rankColor}`}>{i + 1}</span>
                    </div>
                    <div className="p-3">
                      <p className="text-[11px] font-bold text-on-surface truncate mb-3 leading-tight" title={row.ad_name || 'Anúncio'}>{row.ad_name || 'Anúncio Desconhecido'}</p>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                        <div>
                          <p className="text-[9px] font-bold text-secondary uppercase tracking-wide">{metricLabel}</p>
                          <p className="text-sm font-extrabold text-primary">{row.targetMetric.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold text-secondary uppercase tracking-wide">Gasto</p>
                          <p className="text-sm font-bold text-on-surface">R$ {parseFloat(row.spend || 0).toFixed(0)}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold text-secondary uppercase tracking-wide">CPA</p>
                          <p className="text-xs font-bold text-on-surface-variant">R$ {row.cpaTarget}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-bold text-secondary uppercase tracking-wide">CTR</p>
                          <p className="text-xs font-bold text-on-surface-variant">{rowCtr}%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {!isAdLevel && (
          <div className="bg-surface-container-lowest rounded-xl shadow-[0_4px_24px_rgba(25,28,30,0.03)] border border-outline-variant/20 overflow-hidden relative z-10">
            <div className="px-8 py-6 border-b border-outline-variant/20 bg-surface-container-highest/10">
              <h3 className="text-lg font-bold font-headline text-on-surface">{isGeral ? 'Campanhas' : 'Detalhamento de Métricas'}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th
                      className="px-6 py-4 text-xs font-bold font-label uppercase text-secondary cursor-pointer hover:bg-surface-container-low transition-colors group/header"
                      onClick={() => requestSort('campaign_name')}
                    >
                      <div className="flex items-center gap-1">
                        Campanha
                        <span className={`material-symbols-outlined text-[16px] transition-opacity ${sortConfig.key === 'campaign_name' ? 'opacity-100 text-primary' : 'opacity-0 group-hover/header:opacity-100 text-secondary'}`}>
                          {getSortIcon('campaign_name')}
                        </span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold font-label uppercase text-secondary text-right cursor-pointer hover:bg-surface-container-low transition-colors group/header"
                      onClick={() => requestSort('spend')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Gasto
                        <span className={`material-symbols-outlined text-[16px] transition-opacity ${sortConfig.key === 'spend' ? 'opacity-100 text-primary' : 'opacity-0 group-hover/header:opacity-100 text-secondary'}`}>
                          {getSortIcon('spend')}
                        </span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold font-label uppercase text-secondary text-right cursor-pointer hover:bg-surface-container-low transition-colors group/header"
                      onClick={() => requestSort('impressions')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Impressões
                        <span className={`material-symbols-outlined text-[16px] transition-opacity ${sortConfig.key === 'impressions' ? 'opacity-100 text-primary' : 'opacity-0 group-hover/header:opacity-100 text-secondary'}`}>
                          {getSortIcon('impressions')}
                        </span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold font-label uppercase text-secondary text-right cursor-pointer hover:bg-surface-container-low transition-colors group/header"
                      onClick={() => requestSort('clicks')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Cliques
                        <span className={`material-symbols-outlined text-[16px] transition-opacity ${sortConfig.key === 'clicks' ? 'opacity-100 text-primary' : 'opacity-0 group-hover/header:opacity-100 text-secondary'}`}>
                          {getSortIcon('clicks')}
                        </span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold font-label uppercase text-secondary text-right cursor-pointer hover:bg-surface-container-low transition-colors group/header"
                      onClick={() => requestSort('targetMetric')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        {metricLabel}
                        <span className={`material-symbols-outlined text-[16px] transition-opacity ${sortConfig.key === 'targetMetric' ? 'opacity-100 text-primary' : 'opacity-0 group-hover/header:opacity-100 text-secondary'}`}>
                          {getSortIcon('targetMetric')}
                        </span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold font-label uppercase text-secondary text-right cursor-pointer hover:bg-surface-container-low transition-colors group/header"
                      onClick={() => requestSort('cpaTarget')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        CPA
                        <span className={`material-symbols-outlined text-[16px] transition-opacity ${sortConfig.key === 'cpaTarget' ? 'opacity-100 text-primary' : 'opacity-0 group-hover/header:opacity-100 text-secondary'}`}>
                          {getSortIcon('cpaTarget')}
                        </span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((row, idx) => (
                    <tr key={idx} className="bg-surface group/row hover:bg-surface-container-low transition-colors relative">
                      <td className="px-6 py-4 font-headline font-bold text-sm text-on-surface relative">
                        {row.campaign_name || 'Desconhecido'}
                        <div className="hidden group-hover/row:block hover:hidden absolute z-[90] bottom-[90%] left-12 mb-2 bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-2xl border border-slate-200 text-left min-w-[320px] pointer-events-none">
                          <p className="text-xs font-bold text-secondary mb-3 flex items-center gap-1">
                            <span className="material-symbols-outlined text-[14px]">history</span> Comparativo com período anterior
                          </p>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                            <div>
                              <span className="text-[10px] bg-slate-100 px-1 py-0.5 rounded font-bold uppercase tracking-wider text-slate-500 mb-1 inline-block">Gasto</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold">R$ {parseFloat(row.spend || 0).toFixed(2)}</span>
                                {calcDelta(parseFloat(row.spend) || 0, row.prevSpend, true)}
                              </div>
                            </div>
                            <div>
                              <span className="text-[10px] bg-slate-100 px-1 py-0.5 rounded font-bold uppercase tracking-wider text-slate-500 mb-1 inline-block">Cliques</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold">{row.clicks.toLocaleString()}</span>
                                {calcDelta(row.clicks, row.prevClicks, false)}
                              </div>
                            </div>
                            <div>
                              <span className="text-[10px] bg-slate-100 px-1 py-0.5 rounded font-bold uppercase tracking-wider text-slate-500 mb-1 inline-block">{metricLabel}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-primary">{row.targetMetric.toLocaleString()}</span>
                                {calcDelta(row.targetMetric, row.prevTargetMetric, false)}
                              </div>
                            </div>
                            <div>
                              <span className="text-[10px] bg-slate-100 px-1 py-0.5 rounded font-bold uppercase tracking-wider text-slate-500 mb-1 inline-block">CPA</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-on-surface-variant">R$ {row.cpaTarget}</span>
                                {calcDelta(parseFloat(row.cpaTarget) || 0, parseFloat(row.prevCpaTarget) || 0, true)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-secondary text-right">
                        R$ {parseFloat(row.spend || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-secondary text-right">
                        {parseInt(row.impressions || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-secondary text-right">
                        {row.clicks.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-primary text-right">
                        {row.targetMetric.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-on-surface-variant text-right">
                        R$ {row.cpaTarget}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportVisualization;
